<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import { authComputed } from "@/state/helpers";
import store from "@/state/store";
import Multiselect from "vue-multiselect";
import { API } from "@/api";

export default {
  beforeRouteEnter(to, from, next) {
    const userType = store.getters["auth/getUser"].user_type;
    const permissions = store.getters["auth/permissions"];
    if (
      (userType === 1 || userType === 3) &&
      permissions.includes("LEAVE_MANAGEMENT")
    ) {
      next();
    } else {
      alert("You are not allowed to access this page. You will be redirected.");
      next("/");
    }
  },
  page: {
    title: "All Leave Applications",
    meta: [{ name: "description", content: appConfig.description }],
  },
  computed: {
    ...authComputed,
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
  },
  mounted() {
    this.fetchFilterOptions();
    this.fetchApplications();
  },
  watch: {
    currentPage() {
      this.fetchApplications();
    },
    perPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }
      this.fetchApplications();
    },
  },
  methods: {
    fetchFilterOptions() {
      const url = `${this.ROUTES.leaveApplication}/list-options`;
      API.get(url, this.apiConfig())
        .then((res) => {
          const employees = res.data?.employees || [];
          const leaveTypes = res.data?.leaveTypes || [];
          this.employeeOptions = employees.map((employee) => ({
            emp_id: employee.emp_id,
            label: `${employee.emp_first_name || ""} ${
              employee.emp_last_name || ""
            }`.trim(),
            uniqueId: employee.emp_unique_id,
          }));
          this.leaveTypeOptions = leaveTypes.map((leaveType) => ({
            leave_type_id: leaveType.leave_type_id,
            leave_name: leaveType.leave_name,
          }));
        })
        .catch((error) => {
          this.apiErrorHandler(error, "Get Leave Filters Error");
        });
    },
    fetchApplications() {
      const params = new URLSearchParams();
      params.set("page", String(this.currentPage));
      params.set("perPage", String(this.perPage));
      if (this.selectedEmployee?.emp_id) {
        params.set("empId", String(this.selectedEmployee.emp_id));
      }
      if (this.selectedLeaveTypes.length) {
        params.set(
          "leaveTypes",
          this.selectedLeaveTypes.map((type) => type.leave_type_id).join(",")
        );
      }

      const url = `${this.ROUTES.leaveApplication}/list?${params.toString()}`;
      this.loading = true;
      API.get(url, this.apiConfig())
        .then((res) => {
          const payload = res.data || {};
          const rows = payload.data || [];
          const offset = (this.currentPage - 1) * this.perPage;
          this.applications = rows.map((leave, index) => ({
            sn: offset + index + 1,
            ...leave,
          }));
          this.totalRows = payload.total || 0;
        })
        .catch((error) => {
          this.apiErrorHandler(error, "Get Leave Applications Error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyFilters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }
      this.fetchApplications();
    },
    clearFilters() {
      this.selectedEmployee = null;
      this.selectedLeaveTypes = [];
      this.applyFilters();
    },
    selectRow(row) {
      row = row[0];
      if (!row?.leapp_id) return;
      this.leaveAppID = row.leapp_id;
      this.$router.push({
        name: "leave-application-details",
        params: { leaveAppID: this.leaveAppID },
      });
    },
    employeeOptionLabel(option) {
      if (!option) return "";
      return option.uniqueId
        ? `${option.label} (${option.uniqueId})`
        : option.label;
    },
  },
  data() {
    return {
      title: "All Leave Applications",
      items: [
        {
          text: "IHUMANE",
        },
        {
          text: "Dashboard",
          href: "/",
        },
        {
          text: "All Leave Applications",
          active: true,
        },
      ],
      applications: [],
      employeeOptions: [],
      leaveTypeOptions: [],
      selectedEmployee: null,
      selectedLeaveTypes: [],
      loading: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortBy: "sn",
      sortDesc: false,
      fields: [
        { key: "sn", label: "S/n", sortable: true },
        { key: "employee", label: "Employee", sortable: true },
        {
          key: "LeaveType",
          label: "Leave Type",
          sortable: true,
        },
        { key: "leapp_start_date", label: "Start Date", sortable: true },
        { key: "leapp_end_date", label: "End Date", sortable: true },
        { key: "leapp_total_days", label: "Leave Length", sortable: true },
        { key: "Officer", label: "Authorization Officer", sortable: true },
        {
          key: "leapp_status",
          label: "Application Status",
          sortable: true,
        },
      ],
      leaveAppID: null,
    };
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-md-5">
                <div class="form-group">
                  <label>Employee</label>
                  <multiselect
                    v-model="selectedEmployee"
                    :options="employeeOptions"
                    :custom-label="employeeOptionLabel"
                    track-by="emp_id"
                    placeholder="All employees"
                    :allow-empty="true"
                  />
                </div>
              </div>
              <div class="col-md-5">
                <div class="form-group">
                  <label>Leave type</label>
                  <multiselect
                    v-model="selectedLeaveTypes"
                    :options="leaveTypeOptions"
                    label="leave_name"
                    track-by="leave_type_id"
                    placeholder="All leave types"
                    :multiple="true"
                    :close-on-select="false"
                  />
                </div>
              </div>
              <div class="col-md-2 d-flex align-items-end mb-3">
                <b-button
                  variant="success"
                  class="mr-2"
                  :disabled="loading"
                  @click="applyFilters"
                >
                  Apply
                </b-button>
                <b-button variant="light" @click="clearFilters">
                  Clear
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <scale-loader v-if="loading && !applications.length" />
    <div v-else class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    ></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <small class="text-muted">
                  {{ totalRows }} record(s) match the current filters
                </small>
              </div>
            </div>
            <div
              class="table-responsive mb-0"
              :style="loading ? 'opacity: 0.6' : ''"
            >
              <b-table
                ref="donor-table"
                bordered
                selectable
                hover
                :items="applications"
                :fields="fields"
                responsive="sm"
                :per-page="0"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                show-empty
                select-mode="single"
                @row-selected="selectRow"
              >
                <template #cell(employee)="row">
                  <p class="mb-0" v-if="row.value">
                    {{ row.value.emp_first_name }} {{ row.value.emp_last_name }}
                  </p>
                  <small class="text-muted" v-if="row.value">
                    {{ row.value.emp_unique_id }}
                  </small>
                </template>
                <template #cell(LeaveType)="row">
                  <p class="mb-0">
                    {{ row.value && row.value.leave_name }}
                  </p>
                </template>
                <template #cell(leapp_start_date)="row">
                  <span v-if="row.value">
                    {{ new Date(row.value).toDateString() }}
                  </span>
                </template>
                <template #cell(leapp_end_date)="row">
                  <span v-if="row.value">
                    {{ new Date(row.value).toDateString() }}
                  </span>
                </template>
                <template #cell(leapp_total_days)="row">
                  <span> {{ row.value }} days</span>
                </template>
                <template #cell(Officer)="row">
                  <template v-if="row.value">
                    <p class="mb-0">
                      {{ row.value.emp_first_name }}
                      {{ row.value.emp_last_name }}
                    </p>
                    <small class="text-muted">
                      {{ row.value.emp_unique_id }}
                    </small>
                  </template>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(leapp_status)="row">
                  <span v-if="row.value === 0" class="text-warning">
                    PENDING
                  </span>
                  <span v-else-if="row.value === 1" class="text-success">
                    APPROVED
                  </span>
                  <span v-else-if="row.value === 2" class="text-danger">
                    DECLINED
                  </span>
                  <span v-else-if="row.value === 3" class="text-primary">
                    ACTIVE
                  </span>
                  <span v-else-if="row.value === 4" class="text-info">
                    FINISHED
                  </span>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div
                  class="dataTables_paginate paging_simple_numbers float-right"
                >
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
